import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, BadgeCheck } from "lucide-react";

// A continuously-rotating 3D ring of the real catalog vials, each shown as a
// clean product image on a soft champagne backdrop (white composited via multiply).
export default function VialShowcase({ products = [] }) {
  const slots = useMemo(() => products.filter((p) => p.image_url).slice(0, 6), [products]);

  const n = slots.length || 1;
  const radius = Math.round(150 / Math.tan(Math.PI / Math.max(n, 3))) + 70;

  if (!slots.length) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 overflow-hidden" data-testid="vial-showcase-section">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow inline-flex items-center gap-2 justify-center">
          <Sparkles className="w-3.5 h-3.5 text-champagne" /> Celestial Compound Vault
        </span>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
          Every vial, <span className="gold-text">documented &amp; radiant</span>
        </h2>
        <p className="text-muted-foreground mt-4 text-base sm:text-lg leading-relaxed">
          A rotating showcase of precision-synthesized research compounds — hover to pause and explore.
        </p>
      </div>

      <div className="relative mt-4 sm:mt-6">
        {/* ambient glow */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full glow-pulse pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(230,198,101,0.28) 0%, rgba(255,232,235,0.18) 45%, transparent 70%)" }} aria-hidden="true" />

        <div className="vial-stage py-16 sm:py-24" data-testid="vial-showcase-stage">
          <div className="vial-ring">
            {slots.map((s, i) => {
              const angle = (360 / n) * i;
              const verified = s.coa && s.coa.purity;
              return (
                <div key={s.id} className="vial-slot"
                  style={{ transform: `rotateY(${angle}deg) translateZ(${radius}px)` }}>
                  <Link to={`/product/${s.id}`} className="block w-full h-full" data-testid={`vial-card-${s.id}`}>
                    <div className="vial-pedestal" style={{ background: "radial-gradient(circle at 50% 35%, #ffffff 0%, #fbf6ec 70%, #f3ead6 100%)" }}>
                      <div className="vial-halo glow-pulse" aria-hidden="true" />
                      <img src={s.image_url} alt={s.name} loading="lazy"
                        className="absolute inset-0 w-full h-full object-contain p-3"
                        style={{ mixBlendMode: "multiply" }} />
                      {verified && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full border border-[#cfe6cf] bg-white/90 backdrop-blur-sm px-2 py-0.5 text-[10px] font-bold text-[#276434] shadow-sm">
                          <BadgeCheck className="w-3 h-3" /> 99%+
                        </span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-white/95 via-white/70 to-transparent">
                        <p className="font-display text-xl leading-tight text-foreground">{s.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{s.category}</p>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-center -mt-4">
        <Link to="/shop" data-testid="vial-showcase-shop-button"
          className="h-13 px-8 py-3.5 rounded-full btn-gold font-semibold inline-flex items-center gap-2 group">
          See All Options
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </section>
  );
}
